import {
  Component,
  Input,
  Output,
  EventEmitter,
  HostListener,
  ElementRef,
  Injector,
  ApplicationRef,
  ViewChild,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { DropdownOverlayService } from './dropdown-overlay.service';
import { PortalDropdownMenuComponent } from './portal-dropdown-menu.component';

export interface DropdownItem {
  label?: string;
  icon?: string;
  action?: string;
  divider?: boolean;
}

/**
 * SuperDropdownComponent is a dropdown menu component that can be used to display a list of items.
 * It supports keyboard and mouse accessibility, event output, animation, and click outside to close.
 */
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-super-dropdown',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './super-dropdown.component.html',
  styleUrls: ['./super-dropdown.component.css'],
})
export class SuperDropdownComponent implements OnChanges {
  @ViewChild('triggerBtn', { static: false, read: ElementRef })
  triggerBtn!: ElementRef;
  menuOpen = false;
  overlayRef: any = null;
  menuPosition: {
    top: number;
    left: number;
    minWidth: number;
    right?: number;
  } | null = null;
  @Input() align: 'left' | 'right' = 'left';

  /**
   * The list of items to display in the dropdown menu.
   */
  @Input() items: DropdownItem[] = [];

  /**
   * The label to display for the dropdown menu.
   */
  @Input() userLabel = '';

  /**
   * Optional avatar image URL.
   */
  @Input() avatarUrl: string | null = null;

  /**
   * Emits the action of the selected item when an item is clicked.
   */
  @Output() menuAction = new EventEmitter<string>();

  constructor(public overlay: DropdownOverlayService) {}

  ngOnChanges(changes: SimpleChanges): void {
    
  }

  /**
   * The index of the currently focused item.
   */
  focusedIdx = -1;

  /**
   * Toggles the dropdown menu open or closed.
   */
  toggleMenu() {
    console.log('toggleMenu');
    if (this.menuOpen) {
      this.closeMenu();
      return;
    }
    // Calculate trigger position
    const btn = this.triggerBtn?.nativeElement as HTMLElement;
    if (btn) {
      const rect = btn.getBoundingClientRect();
      // Use viewport-relative position for robust overlay placement (fixed to viewport)
      this.menuPosition = {
        top: rect.bottom,
        left: rect.left,
        right: window.innerWidth - rect.right, // <-- add this
        minWidth: rect.width,
      };

      // Optionally, clamp to viewport if needed (not implemented here)

      this.menuOpen = true;
      this.focusedIdx = this.items.findIndex((i) => !i.divider);
      this.overlayRef = this.overlay.attach(PortalDropdownMenuComponent, {
        items: this.items,
        focusedIdx: this.focusedIdx,
        position: this.menuPosition!,
        minWidth: rect.width,
        align: this.align,
      });
      if (this.overlayRef.instance.itemSelected) {
        this.overlayRef.instance.itemSelected.subscribe((idx: number) =>
          this.selectItem(idx)
        );
      }
      if (this.overlayRef.instance.closeMenu) {
        this.overlayRef.instance.closeMenu.subscribe(() => this.closeMenu());
      }
    }
  }

  /**
   * Closes the dropdown menu.
   */
  closeMenu() {
    this.menuOpen = false;
    this.focusedIdx = -1;
    this.overlay.detach();
  }

  /**
   * Selects the item at the specified index and emits the action.
   * @param idx The index of the item to select.
   */
  selectItem(idx: number) {
    if (this.items[idx]?.action) {
      this.menuAction.emit(this.items[idx].action!);
      this.closeMenu();
    }
  }

  /**
   * Moves the focus to the next item in the specified direction.
   * @param dir The direction to move the focus (1 for down, -1 for up).
   */
  nextItem(dir: number) {
    let i = this.focusedIdx;
    do {
      i = (i + dir + this.items.length) % this.items.length;
    } while (this.items[i]?.divider);
    this.focusedIdx = i;
  }

  /**
   * Closes the dropdown menu when a click occurs outside the dropdown.
   * @param event The mouse down event.
   */

  /**
   * Handles keyboard events when the dropdown menu is open.
   * @param event The keyboard event.
   */
  onKeyDown(event: KeyboardEvent) {
    if (!this.menuOpen) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.nextItem(1);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      this.nextItem(-1);
    } else if (event.key === 'Enter') {
      this.selectItem(this.focusedIdx);
    } else if (event.key === 'Escape') {
      this.closeMenu();
    }
  }
}
